import { keys, pick } from 'rambdax';
import Assignees from '@/constants/Assignees';
import translate from '@/lib/intl/translate';
import { dateStringToDate, formatDateToString } from '@/utils/dates';
import { CreateEditDefaultValues } from '../tasksConfig';

export const ENHANCED_ASSIGNEE_OPTIONS = [
  {
    label: translate('CONSTITUENT_ASSIGNEE_ASSIGNED'),
    value: Assignees.ASSIGNED,
  },
  {
    label: translate('CONSTITUENT_ASSIGNEE_UNASSIGNED'),
    value: Assignees.UNASSIGNED,
  },
];

export function convertDeveloperFormValues(developers, assignees) {
  return developers?.reduce?.((memo, developerUserId) => {
    const found = assignees.find(({ userId }) => userId === developerUserId);

    if (!found) {
      return memo;
    }

    return [
      ...memo,
      {
        displayName: found.displayName,
        userId: found.userId,
      },
    ];
  }, []);
}

export function convertConstituentFormValues(constituents) {
  return constituents
    ? [
        {
          constituentId: constituents.value,
          displayName: constituents.text,
        },
      ]
    : [];
}

export function getAPIDataFromForm({ assignees, form, ownerOrgId }) {
  return {
    ...form,
    ownerOrgId,
    constituents: convertConstituentFormValues(form.constituents),
    developers: convertDeveloperFormValues(form.developers, assignees),
    dueDate: form.dueDate ? formatDateToString(form.dueDate) : null,
  };
}

export function getAssigneeOptions({
  assignees,
  currentUserId,
  hasAssigneesOnly = true,
  value,
}) {
  let result = assignees
    .concat(value?.length ? value : [])
    .reduce((arr, { displayName, userId }) => {
      if (userId === currentUserId && !hasAssigneesOnly) {
        return arr;
      }

      return [
        ...arr,
        {
          label: displayName,
          value: userId,
        },
      ];
    }, [])
    .sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });

  if (!hasAssigneesOnly) {
    result = [
      {
        label: translate('CONSTITUENT_ASSIGNED_TO_ME'),
        value: currentUserId,
      },
      ...ENHANCED_ASSIGNEE_OPTIONS,
    ].concat(result);
  }

  // remove any duplicates
  return [...new Map(result.map((item) => [item.value, item])).values()];
}

export function getDefaultValues(task) {
  if (!task) {
    return CreateEditDefaultValues;
  }

  return {
    ...CreateEditDefaultValues,
    ...pick(keys(CreateEditDefaultValues), task),
    // Attachments are written to the API as an array of upload ID strings, but
    // are read as an array of objects.
    attachments: (task?.attachments || []).map(({ uploadId }) => uploadId),
    // The constituents API data is an array but we currently only support
    // having 1 constituent per task.
    constituents: task?.constituents?.length
      ? {
          text: task.constituents[0].displayName,
          value: task.constituents[0].constituentId,
        }
      : null,
    developers: task?.developers
      ? task.developers.map(({ userId }) => userId)
      : [],
    dueDate: task?.dueDate ? dateStringToDate(task.dueDate) : null,
  };
}
